import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { reportAPI, rawMaterialAPI, announcementAPI, accountingAPI } from '../utils/api';
import { Factory, ShoppingCart, FileText, AlertTriangle, Plus, Edit2, Trash2, MessageSquare, TrendingDown, Wallet } from 'lucide-react';
import toast from 'react-hot-toast';
import useAuthStore from '../store/authStore';
import { localName, fmt } from '../utils/helpers';
import SectionHero from '../components/common/SectionHero';

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { user } = useAuthStore();
  const [summary, setSummary] = useState(null);
  const [lowStock, setLowStock] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [period, setPeriod] = useState('monthly');
  const [showAnnForm, setShowAnnForm] = useState(false);
  const [annText, setAnnText] = useState('');
  const [editingAnn, setEditingAnn] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    Promise.all([reportAPI.summary({ period }), accountingAPI.summary().catch(() => ({ data: {} }))])
      .then(([r, a]) => setSummary({ ...r.data, cash_usd: a.data.cash_usd ?? 0 })).catch(() => {});
    rawMaterialAPI.lowStock().then(r => setLowStock(r.data)).catch(() => {});
    announcementAPI.list().then(r => setAnnouncements(r.data)).catch(() => {});
  };

  useEffect(() => { load(); }, [period]); // eslint-disable-line

  const handleSaveAnn = async () => {
    if (!annText.trim()) return;
    setSaving(true);
    try {
      if (editingAnn) { await announcementAPI.update(editingAnn.id, { message: annText }); toast.success(t('success')); }
      else { await announcementAPI.create({ message: annText }); toast.success(t('success')); }
      setAnnText(''); setShowAnnForm(false); setEditingAnn(null); load();
    } catch { toast.error(t('error')); } finally { setSaving(false); }
  };

  const handleDeleteAnn = async (id) => {
    if (!window.confirm(t('confirm_delete'))) return;
    try { await announcementAPI.delete(id); toast.success(t('success')); load(); }
    catch { toast.error(t('error')); }
  };

  const roleColors = { admin: '#ef4444', manager: '#6366f1', sales_manager: '#10b981', production_manager: '#f59e0b' };

  const cards = [
    { key: 'production_batches',  label: t('total_production'), icon: Factory,       color: '#6366f1', grad: 'linear-gradient(135deg,#6366f1,#4338ca)' },
    { key: 'total_sales_amount',  label: t('total_sales'),      icon: ShoppingCart,  color: '#10b981', grad: 'linear-gradient(135deg,#10b981,#059669)', prefix: '$' },
    { key: 'lading_bills',        label: t('lading_bills'),     icon: FileText,      color: '#8b5cf6', grad: 'linear-gradient(135deg,#8b5cf6,#6d28d9)' },
    { key: 'low_stock_alerts',    label: t('low_stock'),        icon: AlertTriangle, color: '#f59e0b', grad: 'linear-gradient(135deg,#f59e0b,#d97706)' },
    { key: 'cash_usd',            label: t('cash_total'),       icon: Wallet,        color: '#14b8a6', grad: 'linear-gradient(135deg,#14b8a6,#0f766e)', prefix: '$' },
    { key: 'total_debt',          label: t('total_debt'),       icon: TrendingDown,  color: '#ef4444', grad: 'linear-gradient(135deg,#ef4444,#b91c1c)', prefix: '$' },
  ];

  const formatDate = (str) => {
    if (!str) return '';
    const d = new Date(String(str).endsWith('Z') ? str : str + 'Z');
    return d.toLocaleDateString(lang === 'fa' ? 'fa-IR' : lang === 'ru' ? 'ru-RU' : 'en-GB', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* PAGE HEADER */}
      <SectionHero title={t('dashboard')} subtitle={t('realtime_data')}>
        <select value={period} onChange={e => setPeriod(e.target.value)} className="input" style={{ width: 140 }}>
          <option value="daily">{t('daily')}</option>
          <option value="weekly">{t('weekly')}</option>
          <option value="monthly">{t('monthly')}</option>
          <option value="yearly">{t('yearly')}</option>
        </select>
      </SectionHero>

      {/* STAT CARDS */}
      <div className="grid-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}>
        {cards.map((card, i) => {
          const Icon = card.icon;
          const raw = summary?.[card.key] ?? 0;
          const value = card.prefix ? `${card.prefix}${fmt(raw)}` : fmt(raw);
          return (
            <div key={i}
              style={{ borderRadius: 18, padding: '20px 22px', position: 'relative', overflow: 'hidden', transition: 'all 0.2s', cursor: 'default', background: 'rgba(255,255,255,0.04)', border: `1px solid ${card.color}22` }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.background = `rgba(255,255,255,0.07)`; e.currentTarget.style.boxShadow = `0 20px 50px rgba(0,0,0,0.5), 0 0 0 1px ${card.color}33`; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              {/* top shimmer line */}
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 1, background: `linear-gradient(90deg, transparent, ${card.color}80, transparent)` }} />
              {/* big glow blob */}
              <div style={{ position: 'absolute', top: -50, right: -50, width: 160, height: 160, borderRadius: '50%', background: card.color, filter: 'blur(60px)', opacity: 0.12, pointerEvents: 'none' }} />

              {/* Icon box */}
              <div style={{ width: 50, height: 50, borderRadius: 15, background: card.grad, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18, boxShadow: `0 8px 24px ${card.color}50`, position: 'relative', zIndex: 1 }}>
                <Icon size={24} color="#fff" strokeWidth={2} />
              </div>

              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: `${card.color}99`, marginBottom: 6, position: 'relative', zIndex: 1 }}>
                {card.label}
              </div>
              <div style={{ fontSize: 38, fontWeight: 800, color: '#fff', letterSpacing: '-2px', lineHeight: 1, position: 'relative', zIndex: 1, fontVariantNumeric: 'tabular-nums' }}>
                {value}
              </div>
            </div>
          );
        })}
      </div>

      {/* LOW STOCK */}
      {lowStock.length > 0 && (
        <div style={{ borderRadius: 18, padding: '18px 20px', background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 1, background: 'linear-gradient(90deg, transparent, rgba(245,158,11,0.5), transparent)' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <div style={{ width: 32, height: 32, borderRadius: 10, background: 'linear-gradient(135deg,#f59e0b,#d97706)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 14px rgba(245,158,11,0.4)' }}>
              <AlertTriangle size={16} color="#fff" />
            </div>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#fbbf24' }}>{t('low_stock_alert')}</span>
            <span style={{ background: 'rgba(245,158,11,0.15)', color: '#fbbf24', border: '1px solid rgba(245,158,11,0.25)', fontSize: 10, fontWeight: 800, padding: '2px 9px', borderRadius: 99 }}>{lowStock.length}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {lowStock.map(m => (
              <div key={m.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: 12, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(245,158,11,0.08)' }}>
                <span style={{ color: '#f1f3ff', fontWeight: 600, fontSize: 13 }}>{localName(m, lang)}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {m.quantity <= 0
                    ? <span className="badge badge-red">{t('stock_out')}</span>
                    : <span className="badge badge-yellow"><TrendingDown size={10} /> {t('low_stock')}</span>
                  }
                  <span style={{ fontSize: 12, color: '#fbbf24', fontWeight: 700 }}>{m.quantity} {m.unit}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ANNOUNCEMENTS */}
      <div style={{ borderRadius: 18, overflow: 'hidden', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', position: 'relative' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 1, background: 'linear-gradient(90deg,transparent,rgba(99,102,241,0.4),rgba(139,92,246,0.3),transparent)' }} />

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 10, background: 'linear-gradient(135deg,#6366f1,#4338ca)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 14px rgba(99,102,241,0.4)' }}>
              <MessageSquare size={15} color="#fff" />
            </div>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{t('announcements')}</span>
            {announcements.length > 0 && (
              <span style={{ background: 'rgba(99,102,241,0.15)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.25)', fontSize: 10, fontWeight: 800, padding: '2px 9px', borderRadius: 99 }}>{announcements.length}</span>
            )}
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => { setShowAnnForm(true); setEditingAnn(null); setAnnText(''); }}>
            <Plus size={13} /> {t('new_message')}
          </button>
        </div>

        {showAnnForm && (
          <div style={{ padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(99,102,241,0.04)' }}>
            <textarea className="input" value={annText} onChange={e => setAnnText(e.target.value)} placeholder={t('write_message')} style={{ minHeight: 70, marginBottom: 10 }} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => { setShowAnnForm(false); setEditingAnn(null); setAnnText(''); }}>{t('cancel')}</button>
              <button className="btn btn-primary btn-sm" onClick={handleSaveAnn} disabled={saving}>{saving ? t('loading') : editingAnn ? t('edit') : t('send')}</button>
            </div>
          </div>
        )}

        <div style={{ padding: announcements.length === 0 ? '44px 20px' : '4px 0' }}>
          {announcements.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'rgba(160,175,220,0.25)' }}>
              <MessageSquare size={32} style={{ margin: '0 auto 10px', opacity: 0.3 }} />
              <p style={{ fontSize: 13 }}>{t('no_messages')}</p>
            </div>
          ) : (
            announcements.map(a => (
              <div key={a.id}
                style={{ padding: '13px 20px', borderBottom: '1px solid rgba(255,255,255,0.04)', display: 'flex', gap: 12, alignItems: 'flex-start', transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.025)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ width: 36, height: 36, borderRadius: 11, background: `linear-gradient(135deg, ${roleColors[a.role] || '#6366f1'}, ${roleColors[a.role] || '#6366f1'}80)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 800, color: '#fff', flexShrink: 0, boxShadow: `0 4px 14px ${roleColors[a.role] || '#6366f1'}35` }}>
                  {a.full_name?.[0]?.toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#f1f3ff' }}>{a.full_name}</span>
                    <span style={{ fontSize: 9, color: roleColors[a.role] || '#818cf8', fontWeight: 800, textTransform: 'uppercase', background: `${roleColors[a.role] || '#6366f1'}18`, padding: '2px 8px', borderRadius: 99 }}>{t('role_' + a.role)}</span>
                    <span style={{ fontSize: 10, color: 'rgba(160,175,220,0.3)', marginInlineStart: 'auto' }}>{formatDate(a.created_at)}</span>
                  </div>
                  <p style={{ fontSize: 13, color: 'rgba(200,210,255,0.55)', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{a.message}</p>
                </div>
                {(a.user_id === user?.id || user?.role === 'admin') && (
                  <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                    <button className="btn btn-ghost btn-icon btn-sm" onClick={() => { setEditingAnn(a); setAnnText(a.message); setShowAnnForm(true); }}><Edit2 size={12} /></button>
                    <button className="btn btn-ghost btn-icon btn-sm" onClick={() => handleDeleteAnn(a.id)} style={{ color: '#f87171' }}><Trash2 size={12} /></button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
